<template>
   <div class="time-stepper" :class="{ active: isActive }">
      <input type="number" :value="displayValue" :min="min" :max="max" :step="step" @input="onInput" @blur="onBlur" @focus="onFocus" @wheel="onWheel" />
      <div class="stepper-actions">
         <button type="button" aria-label="Increase" @click="increase">
            <i class="gts-icon icon-arrow-up icon-xs gts-animation"></i>
         </button>
         <button type="button" aria-label="Decrease" @click="decrease">
            <i class="gts-icon icon-arrow-down icon-xs gts-animation"></i>
         </button>
      </div>
   </div>
</template>

<script>
export default {
   name: "UITimeStepper",
   props: {
      value: {
         type: [String, Number],
         default: "00",
      },
      min: {
         type: Number,
         default: 0,
      },
      max: {
         type: Number,
         default: 59,
      },
      step: {
         type: Number,
         default: 1,
      },
   },
   data() {
      return {
         autoFocusTimeout: null,
         isActive: false,
         scrollAccumulator: 0,
      };
   },
   computed: {
      displayValue() {
         return this.formatValue(this.value);
      },
   },
   methods: {
      formatValue(val) {
         let num = parseInt(val, 10);
         if (isNaN(num)) {
            num = this.min;
         }
         if (num < this.min) {
            num = this.min;
         }
         if (num > this.max) {
            num = this.max;
         }
         return String(num).padStart(2, "0");
      },
      emitValue(val) {
         const formatted = this.formatValue(val);
         this.$emit("input", formatted);
         this.triggerAutoFocus();
      },
      triggerAutoFocus() {
         if (this.autoFocusTimeout) {
            clearTimeout(this.autoFocusTimeout);
         }
         this.autoFocusTimeout = setTimeout(() => {
            this.$emit("auto-next");
         }, 700);
      },
      increase() {
         const current = parseInt(this.displayValue, 10);
         if (current >= this.max) return;
         const next = Math.min(this.max, current + this.step);
         this.emitValue(next);
      },
      decrease() {
         const current = parseInt(this.displayValue, 10);
         if (current <= this.min) return;
         const next = Math.max(this.min, current - this.step);
         this.emitValue(next);
      },
      onInput(event) {
         const val = event.target.value;
         // Eğer değer max'tan büyükse, input'u eski değere geri döndür
         if (val !== "" && parseInt(val, 10) > this.max) {
            event.target.value = this.displayValue;
            return;
         }
         // İki haneli geçerli bir sayı girildiyse otomatik geçiş yap
         if (val.length === 2 && !isNaN(parseInt(val, 10))) {
            const numVal = parseInt(val, 10);
            if (numVal >= this.min && numVal <= this.max) {
               this.emitValue(numVal);
               this.$emit("auto-next");
            }
         }
      },
      onBlur(event) {
         let val = parseInt(event.target.value, 10);
         if (isNaN(val) || val === "") {
            val = this.min;
         }
         val = Math.max(this.min, Math.min(this.max, val));
         this.emitValue(val);
         this.isActive = false;
      },
      selectAll(event) {
         event.target.select();
      },
      onFocus(event) {
         this.selectAll(event);
         this.isActive = true;
      },
      onWheel(event) {
         event.preventDefault();
         this.isActive = true;

         // Scroll değerlerini biriktir
         this.scrollAccumulator += event.deltaY;

         // Belirli bir eşik değerine ulaştığında değişim yap
         const threshold = 10;

         if (Math.abs(this.scrollAccumulator) >= threshold) {
            if (this.scrollAccumulator < 0) {
               this.increase();
            } else {
               this.decrease();
            }
            this.scrollAccumulator = 0;
         }
      },
   },
};
</script>

<style lang="scss" scoped>
.time-stepper {
   display: flex;
   align-items: center;
   border: 2px solid var(--gts-input-border);
   border-radius: 0.5rem;
   background: var(--gts-input-background);
   padding: 0.25rem 0.5rem;

   &:focus-within,
   &.active {
      background: white;
   }

   input {
      width: 72px;
      border: none;
      outline: none;
      font-size: var(--xlarge);
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--gray-700);
      background: transparent;
      text-align: center;
      padding: 0.25rem 0;

      &:focus {
         background: white;
         border: none;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
         -webkit-appearance: none;
         margin: 0;
      }

      &[type="number"] {
         -moz-appearance: textfield;
      }
   }

   .stepper-actions {
      display: flex;
      flex-direction: column;
      margin-left: 0.25rem;

      button {
         background: transparent;
         border: none;
         padding: 0;
         cursor: pointer;
         color: var(--gray-700);

         &:first-child {
            margin-bottom: 0.15rem;
         }
      }
   }
}
</style>
